// InlineEditCell.js
// Round 2 UI fixes (2026-06-07) — Item 10.
//
// Patrón replicado del campo SKU editable de PT v2:
// C:\pulpou\pulpou-pricetrackingv2\src\components\DataTable.tsx (líneas 1613–1685).
//
// UX:
//  - Estado collapsed: muestra valor + ícono lápiz que aparece on-hover (opacity).
//  - Click → input visible con autofocus + botones Guardar (✓ verde) y Cancelar (✕ rojo) inline.
//  - Enter = save, Escape = cancel.
//  - "Click para editar" si el valor está vacío.
//
// Adaptado a CSS puro con prefijo .tm-edit-* (no Tailwind, el repo no lo usa).
// Tipografía 14px mínimo. Soporta type='text' | 'number' y suggestions (datalist).
// `formatDisplay` permite formatear el valor visible (ej. quitar decimales innecesarios
// de CONTENIDO) sin tocar el value real.

import React, { useState, useRef, useEffect } from 'react';
import './InlineEditCell.css';

export default function InlineEditCell({
  label,
  value,
  type = 'text',
  suggestions = [],
  isOverride = false,
  onSave,
  disabled = false,
  disabledReason = null,
  formatDisplay = null, // opcional: (raw) => string
  placeholder = null,   // texto para el modo vacío
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const inputRef = useRef(null);
  const datalistIdRef = useRef(`tm-edit-${(label || 'fld').toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (e) => {
    if (disabled || saving) return;
    e?.stopPropagation();
    setDraft(value ?? '');
    setStatus(null);
    setEditing(true);
  };

  const commit = async (e) => {
    e?.stopPropagation?.();
    const raw = String(draft).trim();
    const oldVal = value === null || value === undefined ? '' : String(value);
    if (raw === oldVal) { setEditing(false); return; }
    setSaving(true); setStatus(null);
    try {
      let out;
      if (type === 'number') {
        if (raw === '') out = null;
        else {
          const parsed = parseFloat(raw.replace(',', '.'));
          if (Number.isNaN(parsed)) throw new Error('Invalid number');
          out = parsed;
        }
      } else {
        out = raw === '' ? null : raw;
      }
      await onSave(out);
      setStatus('success');
      setEditing(false);
      setTimeout(() => setStatus(null), 1500);
    } catch (err) {
      console.error('InlineEditCell save failed:', err);
      setStatus('error');
      setDraft(value ?? '');
      setTimeout(() => setStatus(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const cancel = (e) => {
    e?.stopPropagation?.();
    setDraft(value ?? '');
    setEditing(false);
    setStatus(null);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const isEmpty = value === null || value === undefined || value === '';
  const displayRaw = isEmpty ? '' : String(value);
  const display = formatDisplay && !isEmpty ? formatDisplay(value) : displayRaw;
  const showPlaceholder = isEmpty;
  const placeholderText = placeholder || `Click para editar${label ? ` ${label.toLowerCase()}` : ''}`;

  return (
    <div className={`tm-edit-cell ${isOverride ? 'is-override' : ''} ${disabled ? 'is-disabled' : ''} ${status ? `status-${status}` : ''}`}>
      {label && (
        <div className="tm-edit-label">
          <span className="tm-edit-label-text">{label}</span>
          {isOverride && <span className="tm-edit-override-dot" title="Editado manualmente">●</span>}
        </div>
      )}
      {editing ? (
        <div className="tm-edit-row">
          <input
            ref={inputRef}
            type={type === 'number' ? 'number' : 'text'}
            step={type === 'number' ? '0.01' : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            onClick={(e) => e.stopPropagation()}
            list={suggestions.length > 0 ? datalistIdRef.current : undefined}
            disabled={saving}
            className="tm-edit-input"
            placeholder={label || ''}
          />
          {suggestions.length > 0 && (
            <datalist id={datalistIdRef.current}>
              {suggestions.map(s => <option key={s} value={s} />)}
            </datalist>
          )}
          <button
            type="button"
            className="tm-edit-btn tm-edit-btn-save"
            onClick={commit}
            disabled={saving}
            title="Guardar (Enter)"
            aria-label="Guardar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
          <button
            type="button"
            className="tm-edit-btn tm-edit-btn-cancel"
            onClick={cancel}
            disabled={saving}
            title="Cancelar (Escape)"
            aria-label="Cancelar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      ) : (
        <div
          className="tm-edit-display group"
          onClick={startEdit}
          role={disabled ? undefined : 'button'}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); startEdit(e); } }}
          title={disabled ? (disabledReason || 'Deshabilitado') : `Editar ${label || ''}`.trim()}
        >
          <span className={`tm-edit-value ${showPlaceholder ? 'is-empty' : ''}`}>
            {showPlaceholder ? placeholderText : display}
          </span>
          {!disabled && (
            <button
              type="button"
              className="tm-edit-pencil"
              onClick={startEdit}
              tabIndex={-1}
              aria-label={`Editar ${label || ''}`.trim()}
              title="Editar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
              </svg>
            </button>
          )}
        </div>
      )}
      {status === 'success' && <span className="tm-edit-status success">✓ Guardado</span>}
      {status === 'error' && <span className="tm-edit-status error">Error al guardar</span>}
    </div>
  );
}
